/**
 * RVInvestmentPropertyAllocator – allocate sale proceeds from selling an investment property
 * to village entry fee, bank, downsizer, gifting, funeral bond. Matches Aged Care allocator pattern.
 */
import React from 'react';
import { Wallet, Gift, ShieldCheck, DollarSign } from 'lucide-react';
import { AllocationInput } from '../../components/ui/index.tsx';
import { LedgerRow } from '../../components/narrative/PlainWords.tsx';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import { computeInvestmentPropertyNetForAmount } from '../../../../shared/src/calculation/retirementVillageCalculations.ts';
import { DOWNSIZER_CONTRIBUTION_CAP, FUNERAL_BOND_LIMIT } from '../../constants/index.ts';
import DownsizerRulesAndInsights from '../../components/strategy/DownsizerRulesAndInsights.tsx';
import { strategyExplainers } from '../../strategy/strategyExplainers.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { RvInvestmentPropertyDetails } from './RetirementVillagePage.tsx';

type DraftAsset = FactFindAsset;

export interface RVInvestmentPropertyAllocatorProps {
    factFind: DraftFactFind;
    entryFee: number;
    details: RvInvestmentPropertyDetails;
    setDetails: React.Dispatch<React.SetStateAction<RvInvestmentPropertyDetails>>;
}

function RVInvestmentPropertyAllocator({ factFind, entryFee, details, setDetails }: RVInvestmentPropertyAllocatorProps) {
    const handleDetailChange = (field: string, value: number | undefined) => {
        setDetails(prev => ({ ...prev, [field]: value ?? 0 }));
    };

    const selectedProperty: DraftAsset | undefined = factFind?.assets?.investmentProperty?.find(p => p.id === details.selectedPropertyId)
        || factFind?.assets?.investmentProperty?.[0];
    if (!selectedProperty) {
        return (
            <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200 border-l-4 border-l-amber-400 text-slate-700 text-sm">
                No investment property in the fact find. Add an investment property in Client Profile to use this strategy.
            </div>
        );
    }

    const fullValue = selectedProperty.value || 0;
    // DraftFactFind/DraftAsset are structurally compatible with FactFind/FactFindAsset
    // (see types/formDraft.ts), so this cast is safe.
    const { netProceeds, cgt, sellingCosts } = computeInvestmentPropertyNetForAmount(factFind as unknown as FactFind, selectedProperty as unknown as FactFindAsset, fullValue);

    const totalAllocated = (details.entryFeeAllocation || 0) +
        (details.downsizerContribution || 0) +
        (details.partnerDownsizerContribution || 0) +
        (details.giftingAmount || 0) +
        (details.funeralBondPurchase || 0);
    const unallocatedProceeds = netProceeds - totalAllocated;

    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
    const isCouple = factFind?.adviceFor === 'couple';
    const partnerAge = isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;
    const isDownsizerEligible = clientAge != null && clientAge >= 55;
    const canCommenceAbp = isDownsizerEligible && (clientAge != null && clientAge >= 60 || (partnerAge != null && partnerAge >= 60));

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-6">
            <h4 className="font-semibold text-lg">1. Select investment property</h4>
            {(factFind?.assets?.investmentProperty?.length ?? 0) > 1 ? (
                <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Property to sell</label>
                    <select
                        value={details.selectedPropertyId ?? factFind.assets!.investmentProperty![0]!.id}
                        onChange={(e) => {
                            const id = e.target.value;
                            const prop = factFind.assets!.investmentProperty!.find(p => String(p.id) === id);
                            const salePrice = prop?.value || 0;
                            setDetails(prev => ({
                                ...prev,
                                selectedPropertyId: prop?.id ?? id,
                                salePrice,
                                sellingCosts: salePrice * 0.025,
                                costBase: prop?.costBase ?? 0
                            }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {factFind.assets!.investmentProperty!.map(prop => (
                            <option key={prop.id} value={prop.id}>
                                {prop.description || `Investment Property ${prop.id}`} – {formatCurrency(prop.value || 0)}
                            </option>
                        ))}
                    </select>
                </div>
            ) : (
                <div className="mt-2 p-3 bg-slate-50 border-l-4 border-blue-400 rounded-r-lg text-sm">
                    <p><strong className="text-blue-700">Selected property:</strong> {selectedProperty?.description || 'Investment property'} – {formatCurrency(fullValue)}</p>
                </div>
            )}

            <h4 className="font-semibold text-lg">2. Net proceeds from sale</h4>
            {/* A ledger — the format the owner called the better one — rather than three grey boxes
                and a yellow one (owner, 24 Sep 2026: "the UI for the CGT looks terrible"). */}
            <div className="mt-2 rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-card">
                <LedgerRow label="Sale value" amount={fullValue} />
                <LedgerRow label="Selling costs (est.)" amount={sellingCosts} negative muted />
                <LedgerRow label="Capital gains tax (est.), paid from the proceeds" amount={cgt} negative muted />
                <LedgerRow label="Net proceeds" amount={netProceeds} bold divider />
            </div>

            <h4 className="font-semibold text-lg">3. Allocate the proceeds</h4>
            <div className="mt-2 p-3 bg-slate-50 border-l-4 border-blue-400 rounded-r-lg text-sm flex justify-between items-center">
                <span><strong className="text-blue-700">Unallocated (to bank):</strong></span>
                <span className={`font-bold text-lg ${unallocatedProceeds < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(unallocatedProceeds)}</span>
            </div>
            <div className="mt-4 space-y-4">
                <AllocationInput
                    icon={DollarSign}
                    label="Village entry fee (from sale)"
                    name="entryFeeAllocation"
                    value={details.entryFeeAllocation ?? 0}
                    onChange={handleDetailChange}
                    max={Math.min(netProceeds, entryFee || 0)}
                    tooltip={strategyExplainers.villageEntryFee()}
                />
                <AllocationInput icon={Wallet} label={details.commenceIncomeStream ? 'Downsizer to ABP' : 'Downsizer to super'} name="downsizerContribution" value={details.downsizerContribution} onChange={handleDetailChange} max={Math.min(DOWNSIZER_CONTRIBUTION_CAP, netProceeds)} disabled={!isDownsizerEligible} tooltip={strategyExplainers.downsizerToSuper(isDownsizerEligible)} />
                {isCouple && <AllocationInput icon={Wallet} label={details.commenceIncomeStream ? 'Partner downsizer to ABP' : 'Partner downsizer to super'} name="partnerDownsizerContribution" value={details.partnerDownsizerContribution} onChange={handleDetailChange} max={Math.min(DOWNSIZER_CONTRIBUTION_CAP, netProceeds)} disabled={!isDownsizerEligible} tooltip={strategyExplainers.partnerDownsizerToSuper(isDownsizerEligible)} />}
                <DownsizerRulesAndInsights factFind={factFind} />
                {canCommenceAbp && (
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="rv-ip-commenceIncomeStream"
                            checked={!!details.commenceIncomeStream}
                            onChange={(e) => setDetails(prev => ({ ...prev, commenceIncomeStream: e.target.checked }))}
                        />
                        <label htmlFor="rv-ip-commenceIncomeStream" className="text-sm text-gray-700">Commence an income stream from downsizer contribution</label>
                    </div>
                )}
                <AllocationInput icon={Gift} label="Gift to loved ones" name="giftingAmount" value={details.giftingAmount} onChange={handleDetailChange} max={netProceeds} tooltip={strategyExplainers.giftToLovedOnes()} />
                <AllocationInput icon={ShieldCheck} label="Purchase funeral bond" name="funeralBondPurchase" value={details.funeralBondPurchase} onChange={handleDetailChange} max={FUNERAL_BOND_LIMIT} tooltip={strategyExplainers.funeralBond()} />
            </div>
        </div>
    );
}

export default RVInvestmentPropertyAllocator;
